"use client";

import { c as _c } from "react/compiler-runtime";
import { useTranslations, useLocale } from "next-intl";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useState, useTransition } from "react";
import { useRouter, usePathname } from "@i18n/navigation";
import { useParams } from "next/navigation";
import ReactCountryFlag from "react-country-flag";
import { StateType } from "@lib/hooks/use-toggle-state";
import { Locale } from "@lib/data/locales";
type LanguageOption = {
  code: string;
  name: string;
  //localizedName: string
  countryCode: string;
};
const getCountryCodeFromLocale = (localeCode: string): string => {
  try {
    const locale = new Intl.Locale(localeCode);
    if (locale.region) {
      return locale.region.toUpperCase();
    }
    const maximized = locale.maximize();
    return maximized.region?.toUpperCase() ?? localeCode.toUpperCase();
  } catch {
    const parts = localeCode.split(/[-_]/);
    return parts.length > 1 ? parts[1].toUpperCase() : parts[0].toUpperCase();
  }
};
type LanguageSelectProps = {
  toggleState: StateType;
  locales: Locale[];
  currentLocale: string | null;
};

/**
 * Gets the localized display name for a language code using Intl API.
 * Falls back to the provided name if Intl is unavailable.
 */
const getLocalizedLanguageName = (code: string, fallbackName: string, displayLocale: string = "en-US"): string => {
  try {
    const displayNames = new Intl.DisplayNames([displayLocale], {
      type: "language"
    });
    return displayNames.of(code) ?? fallbackName;
  } catch {
    return fallbackName;
  }
};
const DEFAULT_LOCALE = "es"; // TODO: allow configuration of the locale
const DEFAULT_OPTION: LanguageOption = {
  code: "",
  name: "Default",
  //localizedName: "Default",
  countryCode: ""
};
const LanguageSelect = t0 => {
  const $ = _c(43);
  const {
    toggleState,
    locales,
    currentLocale
  } = t0;
  const t = useTranslations("countries");
  const locale = useLocale();
  const params = useParams();
  const [current, setCurrent] = useState(undefined);
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const pathname = usePathname();
  const {
    state
  } = toggleState;
  let t1;
  if ($[0] !== locales) {
    t1 = locales.map(_temp);
    $[0] = locales;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const localeOptions = t1;
  const options = localeOptions;
  const findDefaultLocale = _temp2;
  let t2;
  if ($[2] !== currentLocale || $[3] !== locale || $[4] !== options) {
    t2 = () => {
      if (currentLocale) {
        const option = options.find(o_1 => o_1.code.toLowerCase() === currentLocale.toLowerCase());
        setCurrent(option ?? findDefaultLocale(options, locale));
      } else {
        if (options.length > 0) {
          const defaultOption = findDefaultLocale(options, locale) ?? options[0];
          if (typeof defaultOption === "undefined") {
            setCurrent(DEFAULT_OPTION);
          } else {
            setCurrent(defaultOption);
          }
        }
      }
    };
    $[2] = currentLocale;
    $[3] = locale;
    $[4] = options;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== currentLocale || $[7] !== options) {
    t3 = [options, currentLocale];
    $[6] = currentLocale;
    $[7] = options;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  if ($[9] !== params || $[10] !== pathname || $[11] !== router) {
    t4 = option_0 => {
      startTransition(async () => {
        router.replace({
          pathname,
          params
        } as any, {
          locale: option_0.code.split("-")[0]
        });
      });
    };
    $[9] = params;
    $[10] = pathname;
    $[11] = router;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleChange = t4;
  let t5;
  if ($[13] !== currentLocale || $[14] !== options) {
    t5 = currentLocale ? options.find(o_2 => o_2.code.toLowerCase() === currentLocale.toLowerCase()) ?? DEFAULT_OPTION : DEFAULT_OPTION;
    $[13] = currentLocale;
    $[14] = options;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t) {
    t6 = t("Language");
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t6) {
    t7 = <span>{t6}:</span>;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== current || $[21] !== isPending || $[22] !== t) {
    t8 = current && <span className="txt-compact-small flex items-center gap-x-2">{current.countryCode && <ReactCountryFlag svg={true} style={{
        width: "16px",
        height: "16px"
      }} countryCode={current.countryCode} />}{isPending ? "..." : t(current.name)}</span>;
    $[20] = current;
    $[21] = isPending;
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t7 || $[25] !== t8) {
    t9 = <ListboxButton className="py-1 w-full"><div className="txt-compact-small flex items-start gap-x-2">{t7}{t8}</div></ListboxButton>;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== options || $[28] !== t) {
    let t11;
    if ($[30] !== t) {
      t11 = o_3 => <ListboxOption key={o_3.code || "default"} value={o_3} className="py-2 hover:bg-gray-200 px-3 cursor-pointer flex items-center gap-x-2">{o_3.countryCode ? <ReactCountryFlag svg={true} style={{
          width: "16px",
          height: "16px"
        }} countryCode={o_3.countryCode} /> : <span style={{
          width: "16px",
          height: "16px"
        }} />}{t(o_3.name)}</ListboxOption>;
      $[30] = t;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    t10 = options.map(t11);
    $[27] = options;
    $[28] = t;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[32] !== t10) {
    t11 = <ListboxOptions className="absolute -bottom-[calc(100%-36px)] left-0 xsmall:left-auto xsmall:right-0 max-h-[442px] overflow-y-scroll z-[900] bg-white drop-shadow-md text-small-regular uppercase text-black no-scrollbar rounded-rounded w-full" static={true}>{t10}</ListboxOptions>;
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== state || $[35] !== t11) {
    t12 = <div className="flex relative w-full min-w-[320px]"><Transition show={state} as={Fragment} leave="transition ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">{t11}</Transition></div>;
    $[34] = state;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== handleChange || $[38] !== isPending || $[39] !== t12 || $[40] !== t5 || $[41] !== t9) {
    t13 = <div><Listbox as="span" onChange={handleChange} defaultValue={t5} disabled={isPending}>{t9}{t12}</Listbox></div>;
    $[37] = handleChange;
    $[38] = isPending;
    $[39] = t12;
    $[40] = t5;
    $[41] = t9;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  return t13;
};
export default LanguageSelect;
function _temp(locale_0) {
  return {
    code: locale_0.code,
    name: locale_0.name,
    countryCode: getCountryCodeFromLocale(locale_0.code)
  };
}
function _temp2(opts, locale_1) {
  const findMatch = target => {
    if (!target) {
      return;
    }
    const normalizedTarget = target.toLowerCase();
    const prefix = normalizedTarget.split("-")[0];
    return opts.find(o_0 => o_0.code.toLowerCase() === normalizedTarget) ?? opts.find(o => o.code.toLowerCase().startsWith(prefix));
  };
  return findMatch(locale_1) ?? findMatch(DEFAULT_LOCALE);
}